/*
 * Portal datasets — platform inventory of which datasets are exposed via
 * which public portals, so platform admins can spot accidental over-sharing
 * (a draft dataset wired to an active public portal) or stale wiring (a
 * dataset that still references a deleted portal).
 *
 * Data sources: /api/saas/public-portal (portal listing) +
 * /api/saas/datasets (dataset listing). One row per (portal, dataset)
 * cross-product so each exposure is its own line.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import TableToolbar from "../../components/TableToolbar.js";
import { useTableState } from "../../lib/useTableState.js";
import { api, ApiError, DatasetDefinition, PublicPortalSite } from "../../lib/api.js";

interface ExposureRow {
  portalId: string;
  portalName: string;
  portalSlug: string;
  portalStatus: PublicPortalSite["status"];
  portalVisibility: PublicPortalSite["visibility"];
  workspaceId: string;
  datasetId: string;
  datasetName: string;
  datasetStatus: string;
  isOrphan: boolean;
}

export default function AdminPortalDatasets() {
  const [portals, setPortals] = useState<PublicPortalSite[] | null>(null);
  const [datasets, setDatasets] = useState<DatasetDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setError(null);
    Promise.all([api.publicPortal(), api.datasets()])
      .then(([p, d]) => { setPortals(p.portals); setDatasets(d.datasets); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }
  useEffect(refresh, []);

  const datasetsById = useMemo(() => {
    const m = new Map<string, DatasetDefinition>();
    for (const d of datasets ?? []) m.set(d.id, d);
    return m;
  }, [datasets]);

  const rows: ExposureRow[] = useMemo(() => {
    const out: ExposureRow[] = [];
    for (const p of portals ?? []) {
      if (p.datasetIds.length === 0) {
        // Portal with no datasets — a sentinel row so it's still visible
        // and the operator can see the portal exists but exposes nothing.
        out.push({
          portalId: p.id, portalName: p.name, portalSlug: p.slug,
          portalStatus: p.status, portalVisibility: p.visibility,
          workspaceId: p.workspaceId,
          datasetId: "—", datasetName: "(no datasets exposed)",
          datasetStatus: "—",
          isOrphan: false
        });
        continue;
      }
      for (const dsId of p.datasetIds) {
        const ds = datasetsById.get(dsId);
        out.push({
          portalId: p.id, portalName: p.name, portalSlug: p.slug,
          portalStatus: p.status, portalVisibility: p.visibility,
          workspaceId: p.workspaceId,
          datasetId: dsId,
          datasetName: ds?.name ?? "(missing dataset)",
          datasetStatus: (ds as { status?: string } | undefined)?.status ?? "—",
          isOrphan: !ds
        });
      }
    }
    return out;
  }, [portals, datasetsById]);

  const t = useTableState(rows as unknown as Array<Record<string, unknown>>, {
    initialPageSize: 25,
    searchFields: ["portalName", "portalSlug", "datasetId", "datasetName", "workspaceId"],
    initialSortKey: "portalName"
  });

  const stats = useMemo(() => {
    const totalExposures = rows.filter((r) => r.datasetId !== "—").length;
    const orphans = rows.filter((r) => r.isOrphan).length;
    const activePortals = (portals ?? []).filter((p) => p.status === "active").length;
    return { totalExposures, orphans, activePortals, portals: portals?.length ?? 0 };
  }, [rows, portals]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Portal datasets</h1>
          <p className="muted">
            Per-portal dataset inventory — every (portal, dataset) exposure rendered as a row so
            you can audit what's reachable from the public web. Orphan rows (dataset id no longer
            exists) are flagged for cleanup.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <Stat label="Portals" value={stats.portals} />
          <Stat label="Active portals" value={stats.activePortals} accent="ok" />
          <Stat label="Dataset exposures" value={stats.totalExposures} />
          <Stat label="Orphan exposures" value={stats.orphans} accent={stats.orphans > 0 ? "warn" : "muted"} />
        </div>
      </section>

      {portals === null ? (
        <p className="muted">Loading…</p>
      ) : portals.length === 0 ? (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No public portals configured</h2>
          <p>
            Public portals expose curated datasets and indicators to the open web (or
            password-restricted audiences). Workspace admins create them under{" "}
            <strong>Datasets &amp; insights → Public portals</strong>.
          </p>
        </div>
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="portal / dataset / workspace…"
            exportFilename="portal-datasets"
            exportColumns={["portalName", "portalSlug", "portalStatus", "portalVisibility", "workspaceId", "datasetId", "datasetName"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Portal</th><th>Slug</th><th>Status</th><th>Visibility</th>
                <th>Workspace</th><th>Dataset</th><th>Dataset name</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as ExposureRow[]).map((r, i) => (
                <tr key={`${r.portalId}-${r.datasetId}-${i}`} style={r.isOrphan ? { background: "#fef2f2" } : undefined}>
                  <td>{r.portalName}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.portalSlug}</td>
                  <td>
                    <span className={`pill ${r.portalStatus === "active" ? "pill-ok" : "pill-muted"}`}>{r.portalStatus}</span>
                  </td>
                  <td>{r.portalVisibility}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.workspaceId}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.datasetId}</td>
                  <td>
                    {r.isOrphan ? <span style={{ color: "#7f1d1d" }}>orphan: {r.datasetId}</span> : r.datasetName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "ok" | "warn" | "muted" }) {
  const color = accent === "ok" ? "#065f46" : accent === "warn" ? "#92400e" : accent === "muted" ? "#475569" : "#1e293b";
  const bg = accent === "ok" ? "#d1fae5" : accent === "warn" ? "#fef3c7" : "#f1f5f9";
  return (
    <div style={{ background: bg, color, padding: "10px 12px", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value.toLocaleString()}</div>
    </div>
  );
}
